// src/pages/Orders/InvoiceDetail.js

import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FaEnvelope, FaPrint, FaDownload, FaChevronLeft, FaCheckCircle } from "react-icons/fa";
import "./InvoiceDetail.css";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import { useCurrency } from '../../context/CurrencyContext';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';
const api = axios.create({ baseURL: API_BASE_URL });

const getAuthToken = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || localStorage.getItem('accessToken');
  if (token) return token;
  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (let key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
      } catch (e) { return storedData; }
    }
  }
  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
  if (imagePath.startsWith('/')) return `${API_BASE_URL}${imagePath}`;
  return `${API_BASE_URL}/${imagePath}`;
};

const getBase64ImageFromUrl = async (imageUrl) => {
  try {
    const res = await fetch(imageUrl);
    if (!res.ok) throw new Error("Image fetch failed");
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Logo load nahi ho saka:", error);
    return null;
  }
};
// ✅ Readable error message from backend (HTML pages avoided)
const extractServerErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('<')) return fallback;
    return trimmed || fallback;
  }
  if (data.error) return data.error;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  return fallback;
};

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const hasBankInfo = (d) => !!(d && (d.bank_name || d.account_number || d.iban || d.account_title));

const InvoiceDetail = () => {
  const { trackingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  const [order, setOrder] = useState(location.state?.order || null);
  const [products, setProducts] = useState(location.state?.products || []);
  const [loading, setLoading] = useState(!location.state?.order);
  const [company, setCompany] = useState(null);
    // ✅ NEW: Email sending state
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState(null);
  const [emailSentAt, setEmailSentAt] = useState(null);

  // Derive shop_id and city from order for company details fetch
  // ✅ FIX: order me shop_id na ho to saved shop use karo (OrderDetail bhi yehi karta hai)
  const orderShopId =
    order?.shop_id ||
    order?.shop?.shop_id ||
    order?.shop_details?.shop_id ||
    (typeof order?.shop === 'number' ? order.shop : null) ||
    localStorage.getItem('selectedShopId') ||
    null;
  const orderCity = order?.city || order?.address?.city || '';

  // Fetch company details scoped by shop_id + city
  useEffect(() => {
    if (!orderShopId) return undefined;

    let cancelled = false;
    const fetchCompany = async () => {
      const urlFor = (city) =>
        `/order/company-invoice-details/?shop_id=${orderShopId}&city=${encodeURIComponent(city || '')}`;

      try {
        let data = null;

        // 1) order ki city ke saath
        try {
          const res = await api.get(urlFor(orderCity));
          data = res.data || null;
        } catch (e) { /* aage try karo */ }

        // 2) bank details khali hon to Company Information page ki saved city ke saath
        const savedCity = localStorage.getItem('selectedCity') || '';
        if (!hasBankInfo(data) && savedCity && savedCity !== orderCity) {
          try {
            const res = await api.get(urlFor(savedCity));
            if (hasBankInfo(res.data)) data = { ...(data || {}), ...res.data };
          } catch (e) { /* aage try karo */ }
        }

        // 3) bina city ke
        if (!hasBankInfo(data)) {
          try {
            const res = await api.get(urlFor(''));
            if (hasBankInfo(res.data)) data = { ...(data || {}), ...res.data };
          } catch (e) { /* ignore */ }
        }

        if (!cancelled) setCompany(data);
      } catch (err) {
        console.warn("Company info not available:", err.message);
        if (!cancelled) setCompany(null);
      }
    };
    fetchCompany();
    return () => { cancelled = true; };
  }, [orderShopId, orderCity]);

  useEffect(() => {
    if (!order) {
      const fetchOrderData = async () => {
        try {
          const response = await api.get(`/order/getAllorder/`);
          const orders = response.data.orders || response.data;
          const foundOrder = orders.find(o => o.order_Tracking_Id === trackingId);

          if (foundOrder) {
            setOrder(foundOrder);
            const prodRes = await api.get(`/order/orderdProducts/${foundOrder.order_Id}`);
            const prodData = Array.isArray(prodRes.data) ? prodRes.data : [];

            const transformed = prodData.map((item) => {
              const baseProduct = item.product_Id || {};
              const imageGallery = baseProduct.imageGallery || {};
              const rawImage = imageGallery.image1 || imageGallery.image2 || imageGallery.image3 || imageGallery.image4 || null;

              let pricePerUnit = 0;
              if (item.price_per_unit !== undefined && item.price_per_unit !== null) {
                pricePerUnit = parseFloat(item.price_per_unit);
              } else if (item.custom_price !== null && item.custom_price !== undefined) {
                pricePerUnit = parseFloat(item.custom_price);
              } else {
                pricePerUnit = parseFloat(baseProduct.product_Price || 0);
              }

              return {
                id: item.id,
                name: baseProduct.product_Name || 'Unnamed Product',
                price: pricePerUnit,
                qty: item.quantity || 0,
                sku: baseProduct.SKU || '',
                productNo: baseProduct.product_Id || '',
                image: getImageUrl(rawImage),
              };
            });
            setProducts(transformed);
          }
        } catch (error) {
          console.error("Error fetching invoice data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchOrderData();
    }
  }, [trackingId, order]);

  if (loading) return <div style={{ padding: '50px', textAlign: 'center', fontSize: '18px', color: '#004aad' }}>Loading Invoice...</div>;
  if (!order) return <div style={{ padding: '50px', textAlign: 'center', fontSize: '18px' }}>Order not found.</div>;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dateString.split('T')[0];
  };

  // ============================================================
  // ✅ MONEY (items se calculate hota hai, order.subtotal na ho tab bhi theek)
  // ============================================================
  const itemsTotal = products.reduce((sum, p) => sum + toNum(p.price) * toNum(p.qty), 0);
  const subtotal = itemsTotal > 0 ? itemsTotal : (parseFloat(order.subtotal) || 0);
  const discount = parseFloat(order.discount) || 0;
  const delivery = parseFloat(order.delivery_Charges) || 0;
  const vat = parseFloat(order.vat_amount) || 0;
  const total = order.total_Amount !== undefined && order.total_Amount !== null
    ? (parseFloat(order.total_Amount) || 0)
    : subtotal + delivery + vat - discount;
  const paymentDue = total;

  // PDF ki built-in font sirf plain characters dikhati hai, is liye symbol saaf karke bhejte hain
  const pdfMoney = (n) => String(formatPrice(n)).replace(/[^\x20-\x7E]/g, '').trim();

  // Net Value ke neeche dikhne wali extra lines (sirf jab hon)
  const extraTotals = [
    delivery > 0 ? { label: 'Delivery Charges', value: delivery, sign: '' } : null,
    vat > 0 ? { label: 'VAT', value: vat, sign: '' } : null,
    discount > 0 ? { label: 'Discount', value: discount, sign: '-' } : null,
  ].filter(Boolean);
  const showGrandTotal = extraTotals.length > 0;

  const invoiceNumber = `INV0${order.order_Id}`;
  const customerName = order.customer
    ? `${order.customer.first_Name || ''} ${order.customer.last_Name || ''}`.trim() || 'N/A'
    : 'N/A';
  const customerEmail = order.customer?.email || '';
  const addressString = order.address
    ? `${order.address.house_Number ? order.address.house_Number + ' ' : ''}${order.address.street_Number ? order.address.street_Number + ' ' : ''}${order.address.area || ''} ${order.address.city || ''}`.trim()
    : 'N/A';

  // Backend field names
  const companyName = company?.company_name || 'Hira Tech International Solutions';
  const companyPhone = company?.phone || '';
  const companyEmail = company?.email || '';
  const companyWebsite = company?.website || 'https://pk.hira-tech.co.uk/';
  const companyAddress1 = company?.address || 'House # 13, Street # 14';
  const companyAddress2 = company?.address_line_2 || 'Sector C1 - Bahria Enclave, Islamabad';
  const bankName = company?.bank_name || '';
  const accountTitle = company?.account_title || '';
  const sortCode = company?.sort_code || '';
  const accountNumber = company?.account_number || '';
  const branch = company?.branch || company?.bank_branch || '';
  const iban = company?.iban || '';
  const bic = company?.bic || '';

  // ✅ Bank details ek hi list se PDF aur screen dono me (Print me ab nahi, Delivery Note hai)
  const bankRows = [
    ['Bank Name', bankName],
    ['Account Title', accountTitle],
    ['Account Number', accountNumber],
    ['Branch', branch],
    ['Sort Code', sortCode],
    ['IBAN', iban],
    ['BIC', bic],
  ].filter(([, v]) => v && String(v).trim());

  const agentName = order.agent_Name || '';
  const agentPhone = order.agent_number || '';
  const courierService = order.courier_service || '';
  const orderNote = order.note || '';
  // ============================================================
  // ✅ NEW: SEND INVOICE EMAIL TO CUSTOMER
  // ============================================================
  const handleSendEmail = async () => {
    // 1. Customer email check
    if (!customerEmail) {
      Swal.fire({
        icon: 'warning',
        title: 'No Email Address',
        text: 'This customer does not have an email address on file. Please update the customer details first.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    // 2. Confirmation dialog
    const confirm = await Swal.fire({
      title: 'Send Invoice Email?',
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.6">
          <p>The invoice <strong>${invoiceNumber}</strong> will be sent to:</p>
          <p style="color:#004aad;font-weight:600;word-break:break-all">${customerEmail}</p>
          <p style="margin-top:8px;color:#64748b;font-size:13px">
            A PDF copy will be attached to the email.
          </p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send Invoice',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    setSendingEmail(true);
    Swal.fire({
      title: 'Sending Invoice...',
      html: `<p style="font-size:14px;color:#64748b">Please wait while we send the email to<br><strong>${customerEmail}</strong></p>`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    try {
      const response = await api.post(
        `/order/invoice/${order.order_Id}/send-email/`,
        { to: customerEmail }
      );

      const sentTo = response.data?.sent_to || customerEmail;

      setEmailSentTo(sentTo);
      setEmailSentAt(new Date().toISOString());

      Swal.fire({
        icon: 'success',
        title: '✅ Invoice Sent Successfully!',
        html: `
          <div style="text-align:left;font-size:14px;line-height:1.7">
            <p>The invoice has been emailed to:</p>
            <p style="color:#16a34a;font-weight:700;word-break:break-all;background:#f0fdf4;padding:8px 12px;border-radius:6px;border:1px solid #bbf7d0">
              ${sentTo}
            </p>
            <p style="margin-top:10px;color:#64748b;font-size:13px">
              📎 PDF invoice is attached.<br>
              If the customer doesn't see it, ask them to check Spam/Junk.
            </p>
          </div>
        `,
        confirmButtonColor: '#004aad',
        confirmButtonText: 'OK',
        timer: 8000,
        timerProgressBar: true
      });

    } catch (error) {
      console.error('Error sending invoice email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Send Email',
        html: `
          <div style="text-align:left;font-size:14px;line-height:1.6">
            <p style="color:#dc2626;font-weight:600">${extractServerErrorMessage(error, 'Could not send the invoice email.')}</p>
            <p style="margin-top:8px;color:#64748b;font-size:13px">
              Please check your email server settings or try again later.
            </p>
          </div>
        `,
        confirmButtonColor: '#004aad'
      });
    } finally {
      setSendingEmail(false);
    }
  };
  // ============================================================
  // DOWNLOAD PDF (Invoice: price + bank details ke saath)
  // ============================================================
  const handleDownload = async () => {
    if (!order) return;

    const doc = new jsPDF("p", "mm", "a4");

    const base64Logo = await getBase64ImageFromUrl("/Hira-Tech-Logo.png");
    if (base64Logo) {
      try {
        doc.addImage(base64Logo, "PNG", 12, 10, 50, 18);
      } catch (e) {
        console.warn("Logo add nahi ho saka PDF mein");
      }
    }

    const headerX = 12;
    let y = 35;

    // Company header
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, headerX, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    if (companyAddress1) { doc.text(companyAddress1, headerX, y); y += 4; }
    if (companyAddress2) { doc.text(companyAddress2, headerX, y); y += 4; }
    if (companyPhone) { doc.text(`Phone: ${companyPhone}`, headerX, y); y += 4; }

    const customerPhone = order.customer ? `${order.customer.phone_Number || ""}` : "";
    const customerEmail = order.customer ? order.customer.email || "" : "";

    // Ship-To-Party (Left side)
    y += 10;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Ship-To-Party", headerX, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(customerName, headerX, y);
    y += 4;

    const addressLines = doc.splitTextToSize(addressString, 80);
    addressLines.forEach((line) => {
      doc.text(line, headerX, y);
      y += 4;
    });

    if (customerPhone) { doc.text(`Contact: ${customerPhone}`, headerX, y); y += 4; }
    if (courierService) { doc.text(`Courier Service: ${courierService}`, headerX, y); y += 4; }

    // Invoice Box (Right side)
    const boxX = 120;
    const boxY = 35;
    const boxWidth = 75;
    const boxHeight = 68;

    doc.setDrawColor(0, 74, 173);
    doc.rect(boxX, boxY, boxWidth, boxHeight);

    doc.setFillColor(0, 74, 173);
    doc.rect(boxX, boxY, boxWidth, 9, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", boxX + 4, boxY + 6);
    doc.text(invoiceNumber, boxX + boxWidth - 4, boxY + 6, { align: "right" });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    let invoiceY = boxY + 15;

    doc.text(`Date: ${formatDate(order.order_Placment_Date)}`, boxX + 4, invoiceY); invoiceY += 5;
    doc.text("Page: 1 of 1", boxX + 4, invoiceY); invoiceY += 5;
    doc.text(`Order ID: ${order.order_Id || ''}`, boxX + 4, invoiceY); invoiceY += 5;
    doc.text(`Tracking ID: ${order.order_Tracking_Id || ''}`, boxX + 4, invoiceY); invoiceY += 5;
    if (courierService) { doc.text(`Courier: ${courierService}`, boxX + 4, invoiceY); invoiceY += 5; }
    if (customerEmail) { doc.text(`Contact: ${customerEmail}`, boxX + 4, invoiceY); invoiceY += 5; }
    doc.text(`Customer Ref: ${order.customer_Reference || "N/A"}`, boxX + 4, invoiceY); invoiceY += 5;

    doc.setDrawColor(0, 74, 173);
    doc.line(boxX, invoiceY, boxX + boxWidth, invoiceY);
    invoiceY += 5;

    if (agentName) { doc.text(`Agent: ${agentName}`, boxX + 4, invoiceY); invoiceY += 5; }
    if (agentPhone) { doc.text(`Mobile: ${agentPhone}`, boxX + 4, invoiceY); }

    // ✅ Products Table (ab Price/SU aur Value ke saath)
    const tableData = products.map((item) => [
      item.productNo || item.sku || "N/A",
      item.name || "N/A",
      item.qty || "N/A",
      pdfMoney(toNum(item.price)),
      pdfMoney(toNum(item.price) * toNum(item.qty))
    ]);

    // ✅ Net Value (aur Delivery / VAT / Discount / Total) table ke andar hi hain,
    // lekin in par koi grid/border nahi: label Price/SU column me, amount Value column me
    const footRows = [
      ['', '', '', 'Net Value:', pdfMoney(subtotal)],
      ...extraTotals.map((t) => ['', '', '', `${t.label}:`, `${t.sign}${pdfMoney(t.value)}`]),
      ...(showGrandTotal ? [['', '', '', 'Total Amount:', pdfMoney(total)]] : []),
    ];

    autoTable(doc, {
      startY: 110,
      head: [["Item", "Material/Description", "Quantity", "Price/SU", "Value"]],
      body: tableData,
      foot: footRows,
      showFoot: 'lastPage',
      theme: "grid",
      // ✅ grid ka colour gehra kiya taake Price/SU aur Value ke darmiyan line saaf dikhe
      styles: { fontSize: 9, cellPadding: 3, lineColor: [140, 140, 140], lineWidth: 0.2 },
      headStyles: { fillColor: "#004aad", textColor: 255, halign: 'left' },
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 28, halign: 'right' },
        4: { cellWidth: 28, halign: 'right' }
      },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index >= 2) {
          data.cell.styles.halign = data.column.index === 2 ? 'center' : 'right';
        }
        if (data.section === 'foot') {
          // ✅ Net Value / Delivery / VAT / Discount / Total par koi grid nahi
          data.cell.styles.lineWidth = 0;
          data.cell.styles.fillColor = [255, 255, 255];

          // Net Value aur Total Amount bold, baqi lines normal
          const isNetValue = data.row.index === 0;
          const isGrandTotal = showGrandTotal && data.row.index === footRows.length - 1;
          if (!isNetValue && !isGrandTotal) {
            data.cell.styles.fontStyle = 'normal';
          }
        }
      }
    });

    const finalY = doc.lastAutoTable.finalY || 110;
    let currentY = finalY + 8;

    // Note Section
    if (orderNote) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Note:", 12, currentY);
      doc.setFont("helvetica", "normal");
      const splitNote = doc.splitTextToSize(orderNote, 180);
      doc.text(splitNote, 12, currentY + 5);
      currentY += (splitNote.length * 5) + 10;
    }

    // Agar neeche jagah kam ho to nayi page
    if (currentY > 235) {
      doc.addPage();
      currentY = 20;
    }

    // Terms
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Terms of payment: 30 days end-of-month", 12, currentY);
    currentY += 10;

    // Footer Line
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(12, currentY, 198, currentY);
    currentY += 6;

    // Footer Info
    doc.setFontSize(8);
    let footerText = [];
    if (companyEmail) footerText.push(`E-Mail: ${companyEmail}`);
    if (footerText.length > 0) {
      doc.text(footerText.join('   |   '), 105, currentY, { align: "center" });
    }

    // ✅ Bank Details
    currentY += 8;
    if (bankRows.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Bank Details:", 12, currentY);
      doc.setFont("helvetica", "normal");
      currentY += 4;

      bankRows.forEach(([label, value]) => {
        doc.text(`${label}: ${value}`, 12, currentY);
        currentY += 4;
      });
    }

    doc.save(`${order.order_Tracking_Id || 'Invoice'}.pdf`);
  };

  // ============================================================
  // ✅ PRINT = DELIVERY NOTE (koi price / totals / bank details nahi)
  // ============================================================
  const handlePrint = async () => {
    if (!order) return;

    const base64Logo = await getBase64ImageFromUrl("/Hira-Tech-Logo.png");

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();

    const customerPhone = order.customer ? `${order.customer.phone_Number || ""}` : "";
    const customerEmail = order.customer ? order.customer.email || "" : "";
    const deliveryNoteNumber = order.order_Tracking_Id || `${order.order_Id}`;

    // Products table: sirf Item / Description / Quantity (poori grid)
    const gridCell = 'border: 1px solid #ccc; padding: 6px; font-size: 9pt;';
    const productsRows = products.map((item) => `
      <tr>
        <td style="${gridCell}">${item.productNo || item.sku || "N/A"}</td>
        <td style="${gridCell}">${item.name || "N/A"}</td>
        <td style="${gridCell} text-align: center;">${item.qty || "N/A"}</td>
      </tr>
    `).join('') || '';

    const logoHTML = base64Logo ? `<img src="${base64Logo}" style="width: 180px; height: auto; display: block;" />` : '';

    const companyAddressBlock = [
      companyAddress1,
      companyAddress2,
      companyPhone ? `Phone: ${companyPhone}` : '',
    ].filter(Boolean).join('<br>');

    const agentBlock = [
      agentName ? `<p>Agent: ${agentName}</p>` : '',
      agentPhone ? `<p>Mobile phone: ${agentPhone}</p>` : '',
    ].filter(Boolean).join('');

    const footerParts = [
      companyWebsite ? `Website: ${companyWebsite}` : '',
      companyEmail ? `E-Mail: ${companyEmail}` : '',
    ].filter(Boolean).join(' - ');

    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Delivery Note - ${deliveryNoteNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A4; margin: 12mm; }
          body { font-family: Arial, Helvetica, sans-serif; background: white; color: #000; line-height: 1.3; }
          .print-container { width: 100%; }

          /* Logo */
          .logo-row { margin-bottom: 14mm; }

          /* Top section: left column (company + ship-to) | right column (delivery note box) */
          .top-section { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 14mm; }
          .left-col { flex: 1; min-width: 0; }

          .company-name { font-size: 18pt; font-weight: bold; margin-bottom: 4px; line-height: 1.2; }
          .company-address { font-size: 10pt; line-height: 1.35; }

          .ship-to-section { margin-top: 14mm; }
          .ship-to-title { font-weight: bold; margin-bottom: 5px; font-size: 11pt; }
          .ship-to-details { font-size: 10pt; line-height: 1.4; }

          .invoice-box { width: 75mm; border: 1px solid #004aad; flex-shrink: 0; }
          .invoice-box-header { background-color: #004aad; color: white; padding: 5px 8px; font-weight: bold; font-size: 12pt; display: flex; justify-content: space-between; }
          .invoice-box-content { padding: 8px; font-size: 10pt; }
          .invoice-box-content p { margin: 2px 0; line-height: 1.3; }
          .divider { border-top: 1px solid #004aad; margin: 5px 0; }

          /* Products table */
          .products-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 0; }
          .products-table th { background-color: #004aad; color: white; padding: 6px; text-align: left; font-size: 10pt; font-weight: bold; border: 1px solid #004aad; }
          .products-table th:nth-child(3) { text-align: center; }
          .products-table td { word-wrap: break-word; }

          /* Note Section */
          .note-section { margin-top: 15px; padding: 10px; background-color: #f9f9f9; border: 1px solid #ddd; border-left: 3px solid #004aad; font-size: 9.5pt; }

          /* Footer */
          .terms { font-size: 8.5pt; margin: 20px 0 5px 0; }
          .footer-info { text-align: center; font-size: 8pt; margin: 10px 0; }

          @media print {
            body { margin: 0; padding: 0; }
            .products-table th { background-color: #004aad !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .invoice-box-header { background-color: #004aad !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div class="print-container">

          <!-- Logo -->
          <div class="logo-row">
            ${logoHTML}
          </div>

          <!-- Top section: company + ship-to on left, delivery note box on right -->
          <div class="top-section">
            <div class="left-col">
              <div class="company-name">${companyName}</div>
              ${companyAddressBlock ? `<div class="company-address">${companyAddressBlock}</div>` : ''}

              <div class="ship-to-section">
                <div class="ship-to-title">Ship-To-Party</div>
                <div class="ship-to-details">
                  ${customerName}<br>
                  ${addressString}<br>
                  ${customerPhone ? `Contact: ${customerPhone}<br>` : ''}
                  ${courierService ? `Courier Service: ${courierService}` : ''}
                </div>
              </div>
            </div>

            <div class="invoice-box">
              <div class="invoice-box-header">
                <span>Delivery Note</span>
                <span>${deliveryNoteNumber}</span>
              </div>
              <div class="invoice-box-content">
                <p>Date: ${formatDate(order.order_Placment_Date)}</p>
                <p>Page: 1 of 1</p>
                <p>Order ID: ${order.order_Id || ''}</p>
                <p>Tracking ID: ${order.order_Tracking_Id || ''}</p>
                ${courierService ? `<p>Courier Service: ${courierService}</p>` : ''}
                ${customerEmail ? `<p>Contact: ${customerEmail}</p>` : ''}
                <p>Customer Reference: ${order.customer_Reference || "N/A"}</p>
                ${agentBlock ? `<div class="divider"></div>${agentBlock}` : ''}
              </div>
            </div>
          </div>

          <!-- Products Table (Item / Description / Quantity only) -->
          <table class="products-table">
            <colgroup>
              <col style="width: 10%;" />
              <col style="width: 76%;" />
              <col style="width: 14%;" />
            </colgroup>
            <thead>
              <tr>
                <th>Item</th>
                <th>Material/Description</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${productsRows}
            </tbody>
          </table>

          <!-- Note Section -->
          ${orderNote ? `
            <div class="note-section">
              <strong>Note:</strong> ${orderNote}
            </div>
          ` : ''}

          <!-- Footer -->
          <div class="terms">Terms of payment: 30 days end-of-month</div>

          <div class="footer-info">
            ${footerParts}
          </div>
        </div>
      </body>
      </html>
    `;

    doc.write(printHTML);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 500);

    iframe.contentWindow.onafterprint = () => {
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);
    };
  };

  return (
    <>
      <div className="invoice-page-container no-print">
        <div className="invd-nav">
          <button className="invd-return" onClick={() => navigate(-1)}>
            <FaChevronLeft /> Return to Order Detail
          </button>
        </div>

        <div className="invd-header">
          <div className="invd-title">
            <div className="invd-line invd-line-main">
              <span className="invd-label">Invoice Number:</span>
              <span className="invd-value">{invoiceNumber}</span>
            </div>
            <div className="invd-line">
              <span className="invd-label">Customer:</span>
              <span className="invd-value">{customerName}</span>
            </div>
            <div className="invd-line">
              <span className="invd-label">Tracking Number:</span>
              <span className="invd-value">{order.order_Tracking_Id}</span>
            </div>

            {/* ✅ Email badge moved inside title block for better UI */}
            {emailSentTo && (
              <div className="email-sent-badge">
                <FaCheckCircle className="email-sent-icon" />
                <span>
                  Invoice emailed to <strong>{emailSentTo}</strong>
                  {emailSentAt && (
                    <span className="email-sent-time">
                      {' '}({new Date(emailSentAt).toLocaleTimeString()})
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="invd-actions">
            <button
              className={`invd-icon invd-email ${sendingEmail ? 'sending' : ''}`}
              title={customerEmail ? `Send invoice to ${customerEmail}` : 'Customer has no email'}
              onClick={handleSendEmail}
              disabled={sendingEmail || !customerEmail}
              style={{
                opacity: (!customerEmail || sendingEmail) ? 0.5 : 1,
                cursor: (!customerEmail || sendingEmail) ? 'not-allowed' : 'pointer'
              }}
            >
              <FaEnvelope />
            </button>
            <button className="invd-icon invd-print" title="Print Delivery Note" onClick={handlePrint}><FaPrint /></button>
            <button className="invd-icon invd-download" title="Download Invoice" onClick={handleDownload}><FaDownload /></button>
          </div>
        </div>

        <div className="invoice-cards-row">
          <div className="section-box">
            <h2 className="section-title">Order Details</h2>
            <div className="details-content">
              <div className="detail-row"><span className="label">Order ID:</span> <span className="value">{order.order_Id}</span></div>
              <div className="detail-row"><span className="label">Tracking ID:</span> <span className="value">{order.order_Tracking_Id}</span></div>
              <div className="detail-row"><span className="label">Order Status:</span> <span className="value">{order.order_Status}</span></div>
              <div className="detail-row"><span className="label">Placement Date:</span> <span className="value">{formatDate(order.order_Placment_Date)}</span></div>
              <div className="detail-row"><span className="label">Delivery Date:</span> <span className="value">{formatDate(order.order_Delivery_Date) || formatDate(order.order_Placment_Date)}</span></div>
              <div className="detail-row"><span className="label">Delivery Method:</span> <span className="value">{order.delivery_Method || ''}</span></div>
              <div className="detail-row"><span className="label">Customer Reference:</span> <span className="value">{order.customer_Reference || ''}</span></div>
              <div className="detail-row"><span className="label">Agent:</span> <span className="value">{order.agent_Name || 'N/A'}</span></div>
              <div className="detail-row"><span className="label">Agent Phone:</span> <span className="value">{order.agent_number || 'N/A'}</span></div>
              {order.note && (
                <div className="detail-row"><span className="label">Note:</span> <span className="value">{order.note}</span></div>
              )}
            </div>
          </div>

          <div className="section-box">
            <h2 className="section-title">Order Amount</h2>
            <div className="details-content">
              <div className="detail-row"><span className="label">Net Value:</span> <span className="value">{formatPrice(subtotal)}</span></div>
              <div className="detail-row"><span className="label">Discount:</span> <span className="value">{formatPrice(discount)}</span></div>
              <div className="detail-row"><span className="label">Delivery Charges:</span> <span className="value">{formatPrice(delivery)}</span></div>
              {vat > 0 && (
                <div className="detail-row"><span className="label">VAT:</span> <span className="value">{formatPrice(vat)}</span></div>
              )}
              <div className="detail-row"><span className="label">Total:</span> <span className="value">{formatPrice(total)}</span></div>
              <div className="detail-row"><span className="label">Payment Due:</span> <span className="value">{formatPrice(paymentDue)}</span></div>

              <div className="status-badge-wrapper">
                <span className={`payment-status ${order.order_Status?.toLowerCase() === 'delivered' ? 'paid' : 'pending'}`}>
                  {order.order_Status || 'Pending'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="products-section">
          <h2 className="section-title">Ordered Products</h2>
          <div className="product-list-container">
            {products.map((product, index) => (
              <div className="invoice-product-item" key={index}>
                <div className="product-image-box">
                  {product.image ? (
                    <img src={product.image} alt={product.name} />
                  ) : (
                    <div className="no-image">No Image</div>
                  )}
                </div>
                <div className="product-details-box">
                  <p className="p-name"><strong>Name:</strong> {product.name}</p>
                  <p className="p-sku"><strong>SKU:</strong> {product.sku || 'N/A'}</p>
                  <p className="p-qty"><strong>Quantity:</strong> {product.qty}</p>
                  <p className="p-price"><strong>Price per unit:</strong> {formatPrice(product.price)}</p>
                  <p className="p-total"><strong>Total Price:</strong> {formatPrice(product.price * product.qty)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="section-box ship-to-section">
          <h2 className="section-title">Ship-To Party</h2>
          <div className="details-content">
            <div className="detail-row"><span className="label">Name:</span> <span className="value">{customerName}</span></div>
            <div className="detail-row"><span className="label">Address:</span> <span className="value">{addressString}</span></div>
            <div className="detail-row"><span className="label">Contact Number:</span> <span className="value">{order.customer?.phone_Number || 'N/A'}</span></div>
            <div className="detail-row"><span className="label">Tracking Number:</span> <span className="value">{order.order_Tracking_Id}</span></div>
          </div>
        </div>

        {/* ✅ Bank Details (screen par bhi) */}
        <div className="section-box ship-to-section">
          <h2 className="section-title">Bank Details</h2>
          <div className="details-content">
            {bankRows.length > 0 ? (
              bankRows.map(([label, value]) => (
                <div className="detail-row" key={label}>
                  <span className="label">{label}:</span> <span className="value">{value}</span>
                </div>
              ))
            ) : (
              <div className="detail-row">
                <span className="value">
                  No bank details saved yet. Add them in Invoice Details (Company Information) and click Save Changes.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceDetail;